// Locale plumbing for the embed bundles (cfb-booking pattern: the host page
// passes only `locale`). Catalog storage lives behind ./catalogs: under
// vitest/tsc that is the full six-locale static table; in the built bundles
// widget/build.mjs substitutes ./catalogs.embed.ts, which inlines only pt and
// fetches the requested locale from <base-url>/embed/locales/<locale>.json.
// The entries await ensureMessages() before mounting React, so messagesFor()
// and embedChromeStrings() stay synchronous for every caller — they just fall
// back to pt when a catalog was never loadable (offline-safe).

import { SUPPORTED_LOCALES, DEFAULT_LOCALE, type Locale } from '../../src/i18n/config';
import { ensureCatalog, getCatalog, type Catalog } from './catalogs';

export function normalizeLocale(raw: string | null | undefined): Locale {
  return (SUPPORTED_LOCALES as readonly string[]).includes(raw ?? '')
    ? (raw as Locale)
    : DEFAULT_LOCALE;
}

export function messagesFor(locale: Locale): Catalog {
  return getCatalog(locale);
}

/**
 * Make `locale`'s catalog available to the synchronous accessors below.
 * Resolves immediately for pt (always inlined) and already-loaded locales;
 * never rejects — an unreachable catalog resolves and leaves the pt fallback
 * in place. `baseUrl` is the admin origin the bundle was served from (the
 * element's `base-url` attribute, same origin the stylesheet loads from).
 */
export function ensureMessages(locale: Locale, baseUrl: string): Promise<void> {
  return ensureCatalog(locale, baseUrl);
}

/**
 * Strings for the pre-mount chrome (loading / unavailable panels in
 * widget/src/status.tsx). Plucked from the catalogs directly because these
 * states render before — or instead of — the NextIntlClientProvider mount.
 */
export interface EmbedChromeStrings {
  loading: string;
  retry: string;
  bookingUnavailableTitle: string;
  bookingUnavailableBody: string;
  formUnavailableTitle: string;
  formUnavailableBody: string;
}

export function embedChromeStrings(locale: Locale): EmbedChromeStrings {
  const cat = getCatalog(locale);
  const widget = cat.widget as {
    loading: string;
    outage: { title: string; bodyNoPhone: string; retry: string };
  };
  const contact = cat.contact as { unavailable: { title: string; body: string } };
  return {
    loading: widget.loading,
    retry: widget.outage.retry,
    bookingUnavailableTitle: widget.outage.title,
    bookingUnavailableBody: widget.outage.bodyNoPhone,
    formUnavailableTitle: contact.unavailable.title,
    formUnavailableBody: contact.unavailable.body,
  };
}
